import { isDev } from "@/constants/env";
import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";

type LocaleContext = {
    localeCode: "ar" | "en";
    localeDirection: "rtl" | "ltr";
    t: (phrase: string) => string;
    setLocale: (code: "ar" | "en") => void;
};

const localeContext = createContext(null as null | LocaleContext);
const defaultLocale = "ar";

const localesMap: {
    [locale: string]: {
        direction: "rtl" | "ltr";
        translations: {
            [phrase: string]: string;
        };
    };
} = {
    ar: {
        direction: "rtl",
        translations:Object.fromEntries( Object.entries({
            submit: "تاكيد",
            "first name": "الاسم الأول",
            "last name": "اسم العائلة",
            "national id": "الرقم الوطني",
            "phone number": "رقم الهاتف",
            "invalid passport number": "رقم جواز السفر غير صالح",
            light: "فاتح",
            system: "النظام",
            "Success!! your request has been sent, we will be in contact as soon as possible": "تمت العملية بنجاح!! تم إرسال طلبك، سنتواصل معك في أقرب وقت ممكن.",
            dark: "داكن",
            language: "اللغة",
            "invalid national id": "الرقم الوطني غير صالح",
            reason: "السبب",
            "invalid firstname": "الاسم الأول غير صالح",
            "invalid lastname": "اسم العائلة غير صالح",
            "too small: expected string to have >=5 characters":
                "قصير جدًا: يجب أن يحتوي النص على 5 أحرف على الأقل",
            "phone invalid": "رقم الهاتف غير صالح",
            passport: "جواز السفر",
            "passport number": "رقم جواز السفر",
            "identity type": "نوع الهوية",
        }).map(([phrase, translation])=>{
            return [phrase.toLowerCase(), translation]
        })),
    },
    en: {
        direction: "ltr",
        translations: {},
    },
};

const localeStorageKey = "locale";
const getLocale = () => {
    const storedValue = localStorage.getItem(localeStorageKey);
    if (storedValue) {
        return storedValue as "ar" | "en";
    }
    return defaultLocale as "ar" | "en";
};

const createBatcher = <T,>(props: {
    timeout?: number;
    period: number;
    protected?: boolean;
    handler: (items: T[], abortSignal: AbortSignal) => void | Promise<void>;
}) => {
    let pendingItems = [] as T[];
    let running = false;
    let periodHolder = undefined as undefined | ReturnType<typeof setTimeout>;
    let lastTimeRanTimestamp = null as null | number;

    const cycle = async () => {
        if (running) {
            return;
        }
        running = true;
        const items = pendingItems;
        pendingItems = [];
        const abortController = new AbortController();
        let timeotuHolder = null as null | ReturnType<typeof setTimeout>;
        if (props.timeout) {
            timeotuHolder = setTimeout(() => {
                abortController.abort();
            }, props.timeout);
        }

        try {
            await props.handler(items, abortController.signal);
        } catch (error) {
            console.error(error);
        }

        if (timeotuHolder) {
            clearTimeout(timeotuHolder);
        }

        running = false;
        if (pendingItems.length && !periodHolder) {
            attemptReschedule();
        }
    };

    const attemptReschedule = () => {
        if (running && props.protected) {
            if (periodHolder) {
                clearTimeout(periodHolder);
            }
            return;
        }
        const now = Date.now();
        if (
            periodHolder &&
            lastTimeRanTimestamp &&
            now - lastTimeRanTimestamp > props.period
        ) {
            return;
        }

        if (periodHolder) {
            clearTimeout(periodHolder);
        }
        setTimeout(cycle, props.period);
    };

    const push = (item: T) => {
        pendingItems.push(item);
        attemptReschedule();
    };

    return {
        push,
    };
};

const translationBatcher = createBatcher<{
    phrase: string;
    locale: string;
}>({
    handler: async (translations, signal) => {
        if (!isDev) {
            return;
        }
        const headers = new Headers();
        headers.set("Content-Type", "application/json");
        await fetch("http://localhost:3421", {
            signal,
            method: "POST",
            body: JSON.stringify({ translations }),
            headers: headers,
        });
    },
    period: 1e3,
    protected: true,
    timeout: 10e3,
});

export const LocaleProvider = (props: { children: React.ReactNode }) => {
    const [currentLocale, setLocale] = useState(() => {
        return getLocale();
    });
    const locale = useMemo(() => {
        return localesMap[currentLocale] ?? localesMap[defaultLocale]!;
    }, [currentLocale]);

    const attemptReport = useCallback((phrase: string, localeCode: string) => {
        translationBatcher.push({ phrase, locale: localeCode });
    }, []);

    const t = useCallback(
        (phrase: string): string => {
            if (!phrase) {
                return phrase;
            }
            const phraseLower = phrase.toLowerCase();
            const exactTranslation = locale.translations[phraseLower];

            if (exactTranslation) {
                return exactTranslation;
            }

            if (phrase.match(/{{.*?}}/)) {
                const matches = [
                    ...phrase.matchAll(
                        /(?:\{\{(.*?)\}\})|(?:(.*?)(?=$|\{\{))/gi,
                    ),
                ];
                const translations = [] as string[];
                console.log(matches);
                for (const match of matches) {
                    if (match[1]) {
                        translations.push(match[1]);
                        continue;
                    }
                    const translatable = match[2]!;
                    const preceedingSpaces = translatable.match(/^\s+/);
                    if (preceedingSpaces) {
                        translations.push(preceedingSpaces[0]);
                    }
                    translations.push(t(translatable.trim()));

                    const leadingSpaces = translatable.match(/\s+$/);
                    if (leadingSpaces) {
                        translations.push(leadingSpaces[0]);
                    }
                }
                return translations.join("");
            }

            if (currentLocale != "en") {
                attemptReport(phraseLower, currentLocale);
            }

            return phrase;
        },
        [locale],
    );

    return (
        <localeContext.Provider
            value={{
                localeCode: currentLocale,
                localeDirection: locale.direction,
                setLocale(code) {
                    localStorage.setItem(localeStorageKey, code);
                    setLocale(code);
                },
                t,
            }}
        >
            {props.children}
        </localeContext.Provider>
    );
};

export const useLocale = () => {
    const locale = useContext(localeContext);
    if (!locale) {
        throw new Error("No Locale Provider Found");
    }
    return locale;
};
